import React, { useLayoutEffect, useRef, useState } from 'react';
import {
  CHALLENGE_COURSE_SPEND_OPTIONS,
  CHALLENGE_INVESTING_EXPERIENCE_OPTIONS,
  type ChallengeCourseSpend,
  type ChallengeInvestingExperience,
} from './challengeQualifyingLead';
import './ChallengeQualifyingStep.css';

export type ChallengeQualifyingStepAnswers = {
  investingExperience?: ChallengeInvestingExperience;
  courseSpend?: ChallengeCourseSpend;
};

const INVESTING_QUESTION = 'How long have you been investing or trading?';
const SPEND_QUESTION = 'How much have you spent on trading or investing courses?';

function OptionButtons<T extends string>({
  options,
  selected,
  onChoose,
}: {
  options: readonly { readonly value: T; readonly label: string }[];
  selected: T | null;
  onChoose: (value: T) => void;
}): React.ReactElement {
  return (
    <div className="chq-options">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="chq-option"
          aria-pressed={selected === option.value}
          data-value={option.value}
          onClick={() => onChoose(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Step 2 of the free challenge signup (Rob, 2026-09-30). The seat is already
 * saved before this shows. Two tap questions, one at a time, no dropdowns and
 * no skip link. Every tap reports the answers so far; the page finishes the
 * step once both are in.
 */
export default function ChallengeQualifyingStep({
  onAnswer,
}: {
  onAnswer: (
    answers: ChallengeQualifyingStepAnswers,
    changed: keyof ChallengeQualifyingStepAnswers,
  ) => void;
}): React.ReactElement {
  const [investingExperience, setInvestingExperience] =
    useState<ChallengeInvestingExperience | null>(null);
  const [courseSpend, setCourseSpend] = useState<ChallengeCourseSpend | null>(null);
  const [questionIndex, setQuestionIndex] = useState<0 | 1>(0);
  const completed = useRef(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Before the first paint (layout effect), so the answers never appear at the
  // old scroll position and then jump while someone is already tapping. The
  // seat form sat lower on the page; start step 2 at the top so the heading
  // and every answer are on the first phone screen.
  useLayoutEffect(() => {
    window.scrollTo?.({ top: 0, left: 0, behavior: 'auto' });
    headingRef.current?.focus({ preventScroll: true });
  }, []);

  const chooseInvesting = (value: ChallengeInvestingExperience) => {
    if (completed.current) return;
    setInvestingExperience(value);
    const answers: ChallengeQualifyingStepAnswers = { investingExperience: value };
    if (courseSpend) {
      answers.courseSpend = courseSpend;
      completed.current = true;
    } else setQuestionIndex(1);
    onAnswer(answers, 'investingExperience');
  };

  const chooseSpend = (value: ChallengeCourseSpend) => {
    if (completed.current) return;
    setCourseSpend(value);
    const answers: ChallengeQualifyingStepAnswers = { courseSpend: value };
    if (investingExperience) {
      answers.investingExperience = investingExperience;
      completed.current = true;
    }
    onAnswer(answers, 'courseSpend');
  };

  const questionKey = questionIndex === 0 ? 'investingExperience' : 'courseSpend';

  return (
    <section
      className="chq-step"
      data-testid="challenge-qualifying-step"
      data-question={questionKey}
      aria-labelledby="chq-heading"
    >
      <p className="chq-saved" role="status">
        Your free seat is saved.
      </p>
      <h1 id="chq-heading" ref={headingRef} tabIndex={-1}>
        2 quick questions so Rob can tailor the show
      </h1>
      <p className="chq-progress">Question {questionIndex + 1} of 2</p>
      <fieldset className="chq-question" data-testid={`challenge-qualifying-${questionKey}`}>
        <legend>{questionIndex === 0 ? INVESTING_QUESTION : SPEND_QUESTION}</legend>
        {questionIndex === 0 ? (
          <OptionButtons
            options={CHALLENGE_INVESTING_EXPERIENCE_OPTIONS}
            selected={investingExperience}
            onChoose={chooseInvesting}
          />
        ) : (
          <OptionButtons
            options={CHALLENGE_COURSE_SPEND_OPTIONS}
            selected={courseSpend}
            onChoose={chooseSpend}
          />
        )}
      </fieldset>
      {questionIndex === 1 ? (
        <button type="button" className="chq-back" onClick={() => setQuestionIndex(0)}>
          Back to question 1
        </button>
      ) : null}
    </section>
  );
}
